import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { DEFAULT_PAGE, DEFAULT_TOTAL_PAGES, VPS_OS, VPS_Status } from "@app/constants";
import OrderVpsService from "@app/services/http/order.vps.service";
import OrderService from "@app/services/http/order.service";
import { openPortalDialog } from "@app/services/modal.service";
import OrderVpsHistoryModal from "@app/components/order-vps-history-modal";
import { ComputerConfig, OrderSummary, OrderVps } from "@app/types";
import { formatMoneyVN } from "@app/utils/util";
import useObservable from "@core/hooks/use-observable.hook";
import { useT } from "@app/contexts/preferences.context";
import { getOrderVpsEnd, isWaiting, remoteOf } from "@app/pages/vps-rental/vps-orders";
import OrderStats from "./order-stats";
import { Cell, HistoryCell, NameCell, OrdersShell, Row, StateCell, useDebounced } from "./orders-shell";

type Filter = "" | VPS_Status;

const HEADERS = ["VPS", "Cấu hình", "Thời gian thuê", "Bắt đầu → Kết thúc", "Tổng tiền", "Trạng thái"];

/** Tổng tiền đã trả; đơn cũ chưa lưu total_price thì ước tính theo giá tháng (giống API summary) */
const totalOf = (order: OrderVps) =>
  order.total_price ||
  Math.max(0, ((order.vps?.config as ComputerConfig)?.monthly_rental_price || 0) * order.rental_period - (order.discount || 0));

/** Đơn VPS: OrderVpsService.getOrdersVps (thêm keyword, startFrom, startTo) */
function VpsOrders() {
  const t = useT();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [orders, setOrders] = useState<OrderVps[]>([]);
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<Filter>("");
  // "Xem đơn" ở Biến động số dư mở trang này với ?type=vps&q=<mã> để tìm sẵn đơn tương ứng
  const [params] = useSearchParams();
  const [q, setQ] = useState(() => ((params.get("type") || "pc") === "vps" ? params.get("q") || "" : ""));
  const keyword = useDebounced(q);
  const [startFrom, setStartFrom] = useState("");
  const [startTo, setStartTo] = useState("");
  const [summary, setSummary] = useState<OrderSummary>();

  const { subscribeUntilDestroy, subscribeOnce } = useObservable();

  useEffect(() => {
    subscribeOnce(OrderService.getOrderSummary("vps"), setSummary);
  }, []);

  useEffect(() => {
    subscribeUntilDestroy(
      OrderVpsService.getOrdersVps(page, "", "", "", status || undefined, { keyword, startFrom, startTo }),
      (res) => {
        setOrders(res?.data || []);
        setTotalPages(res?.total_page || DEFAULT_TOTAL_PAGES);
        setCount(res?.count || 0);
        setLoaded(true);
      }
    );
  }, [page, keyword, startFrom, startTo, status]);

  return (
    <>
      <OrderStats summary={summary} />
      <OrdersShell<Filter>
        search={{
          value: q,
          placeholder: "Tìm theo cấu hình, mã VPS, IP…",
          onChange: (v) => {
            setQ(v);
            setPage(DEFAULT_PAGE);
          },
        }}
        dates={{
          from: startFrom,
          to: startTo,
          onChange: (from, to) => {
            setStartFrom(from);
            setStartTo(to);
            setPage(DEFAULT_PAGE);
          },
        }}
        tabs={{
          value: status,
          onChange: (v) => {
            setStatus(v);
            setPage(DEFAULT_PAGE);
          },
          items: [
            { value: "", label: "Tất cả", count: summary?.total },
            { value: VPS_Status.ON_RENTAL, label: "Đang hoạt động", count: summary?.active },
            { value: VPS_Status.END, label: "Đã kết thúc", count: summary?.ended },
          ],
        }}
        headers={HEADERS}
        loading={!loaded}
        empty={!orders.length}
        filtered={!!keyword || !!startFrom || !!startTo || !!status}
        page={page}
        totalPages={totalPages}
        count={count}
        onPageChange={setPage}
      >
        {orders.map((order) => {
          const config = order.vps?.config as ComputerConfig | undefined;
          // quá ngày kết thúc mà cron server chưa đổi status cũng coi là đã kết thúc
          const ended =
            order.status === VPS_Status.END ||
            (order.status === VPS_Status.ON_RENTAL && getOrderVpsEnd(order).valueOf() <= Date.now());
          const waiting = !ended && isWaiting(order);
          const vpsId = `VPS_${order.vps?._id}`;

          return (
            <Row key={order._id} ended={ended}>
              <NameCell
                label={HEADERS[0]}
                name={order.name || config?.config_name || "VPS"}
                id={vpsId}
                ip={!ended && !waiting ? remoteOf(order) : ""}
              />
              <Cell label={HEADERS[1]}>
                <b className="text-fg">{config?.config_name || "—"}</b>
                <small className="text-xs text-muted">
                  {order.vps?.os === VPS_OS.UBUNTU ? "Ubuntu" : "Windows"} · {formatMoneyVN(config?.monthly_rental_price || 0)}đ/
                  {t("tháng")}
                </small>
              </Cell>
              <Cell label={HEADERS[2]} className="tabular-nums">
                <b className="text-fg">
                  {order.rental_period} {t("tháng")}
                </b>
              </Cell>
              <Cell label={HEADERS[3]} className="o-d whitespace-nowrap tabular-nums">
                <span>{dayjs(order.time_start).format("DD/MM/YYYY HH:mm:ss")}</span>
                <span>{getOrderVpsEnd(order).format("DD/MM/YYYY HH:mm:ss")}</span>
              </Cell>
              <Cell label={HEADERS[4]} className="tabular-nums">
                <b className="text-fg">{formatMoneyVN(totalOf(order))}đ</b>
                {!!order.discount && (
                  <small className="text-xs text-ok">
                    {t("Chiết khấu")} {formatMoneyVN(order.discount)}đ
                  </small>
                )}
              </Cell>
              <StateCell
                label={HEADERS[5]}
                tone={ended ? "muted" : waiting ? "warn" : "ok"}
                text={ended ? "Kết thúc" : waiting ? "Đang chờ" : "Đang thuê"}
              />
              <HistoryCell onClick={() => openPortalDialog(OrderVpsHistoryModal, { order_id: order._id, title: vpsId })} />
            </Row>
          );
        })}
      </OrdersShell>
    </>
  );
}

export default VpsOrders;
